import AppIconButton from "./AppIconButton";
import { useMemo, useState } from "react";
import { Box, FormControl, FormHelperText, FormLabel, InputAdornment, Paper, Popover, Stack, Tooltip, Typography } from "@mui/material";
import AppTextField from "./AppTextField";
import type { AppTextFieldProps } from "./constants";
import { appFormFieldSx, chartOptionFieldLabelSx, chartOptionInputSx, FORM_CONTROL_MIN_HEIGHT } from "../../lib/ui/formLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const DEFAULT_SWATCHES = [
  "#5470c6",
  "#91cc75",
  "#fac858",
  "#ee6666",
  "#73c0de",
  "#3ba272",
  "#fc8452",
  "#9a60b4",
  "#ea7ccc",
  "#1f2937",
  "#64748b",
  "#e2e8f0",
  "#ffffff",
  "#000000",
];

function normalizeHex(raw: string): string | null {
  const value = raw.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(value)) return value.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/.test(value)) return `#${value.toLowerCase()}`;
  return null;
}

type Props = Omit<AppTextFieldProps, "value" | "onChange"> & {
  value?: string;
  onChange: (value: string) => void;
  swatches?: string[];
  pickColorLabel?: string;
  /** Inline swatch + short hex input instead of full-width text field. */
  compact?: boolean;
};

function SwatchButton({
  color,
  disabled,
  pickColorLabel,
  onClick,
}: {
  color: string;
  disabled?: boolean;
  pickColorLabel: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  const custom = useAppCustomStyles();
  return (
    <Tooltip title={pickColorLabel}>
      <AppIconButton
        size="small"
        disabled={disabled}
        aria-label={pickColorLabel}
        onClick={onClick}
        sx={custom.components.appColorField.swatchButton(color)}
      />
    </Tooltip>
  );
}

function ColorSwatchPopover({
  anchorEl,
  onClose,
  swatches,
  pickColorLabel,
  onPick,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  swatches: string[];
  pickColorLabel: string;
  onPick: (color: string) => void;
}) {
  const custom = useAppCustomStyles();
  const styles = custom.components.appColorField;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          elevation: 8,
          sx: styles.popoverPaper,
        },
      }}
    >
      <Paper elevation={0} sx={styles.popoverContent}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={styles.popoverTitle}>
          {pickColorLabel}
        </Typography>
        <Box sx={styles.swatchGrid}>
          {swatches.map((color) => (
            <AppIconButton
              key={color}
              size="small"
              aria-label={color}
              onClick={() => {
                onPick(color);
                onClose();
              }}
              sx={styles.swatchItem(color)}
            />
          ))}
        </Box>
      </Paper>
    </Popover>
  );
}

export default function AppColorField({
  value = "",
  onChange,
  swatches = DEFAULT_SWATCHES,
  pickColorLabel = "Pick color",
  label,
  helperText,
  disabled,
  compact = false,
  ...rest
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const previewColor = useMemo(() => normalizeHex(value) ?? "#5470c6", [value]);
  const custom = useAppCustomStyles();
  const styles = custom.components.appColorField;

  if (compact) {
    return (
      <FormControl size="small" margin="dense" disabled={disabled} sx={styles.compactFormControlMerged}>
        {label ? (
          <FormLabel sx={chartOptionFieldLabelSx}>{label}</FormLabel>
        ) : null}
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={styles.compactInput}
        >
          <SwatchButton
            color={previewColor}
            disabled={disabled}
            pickColorLabel={pickColorLabel}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          />
          <AppTextField
            {...rest}
            hiddenLabel
            label={label || pickColorLabel}
            size="small"
            margin="none"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            sx={styles.compactTextFieldMerged}
          />
        </Stack>
        {helperText ? <FormHelperText sx={styles.helperText}>{helperText}</FormHelperText> : null}
        <ColorSwatchPopover
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          swatches={swatches}
          pickColorLabel={pickColorLabel}
          onPick={onChange}
        />
      </FormControl>
    );
  }

  return (
    <>
      <AppTextField
        {...rest}
        label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SwatchButton
                color={previewColor}
                disabled={disabled}
                pickColorLabel={pickColorLabel}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              />
            </InputAdornment>
          ),
        }}
      />
      <ColorSwatchPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        swatches={swatches}
        pickColorLabel={pickColorLabel}
        onPick={onChange}
      />
    </>
  );
}
